import Xhr from 'xhr'
import utils from './utils'
import EventEmitter from './utils/EventEmitter'

// let XhrFactory = XMLHttpRequest

// tslint:disable-next-line:strict-type-predicates
// if (typeof XhrFactory === 'undefined') {
//   XhrFactory = Xhr as any
// }
if (typeof utils.global.XMLHttpRequest === 'undefined') {
  XMLHttpRequest = Xhr as any
}

const STATUS_OK = 200
const REQUEST_TIMEOUT = 120000
const XHR_READY_STATE_SUCCESS = 4

export class ErrorResponse {
  constructor(public code: string | number, public message: string) {}
}

type onRequestHandler = (request: Request) => void
type onResponseHandler = (
  response: Promise<any>,
  request: Request
) => Promise<any>
type ResponseType = '' | 'text' | 'json' | 'arraybuffer' | 'blob' | 'document'

export interface HttpRequestOptions {
  url: string
  method?: string
  query?: string | {}
  headers?: { [name: string]: string }
  body?: any
  timeout?: number
}

export default class Request extends EventEmitter {
  private readonly _headers: {}
  private _url: string
  private _method: string
  private _query: string | {}
  private _body: any
  private _timeout: number
  private _responseType: ResponseType
  private _onBeforeHandlers: onRequestHandler[]
  private _onAfterHandlers: onResponseHandler[]
  private _xhr?: XMLHttpRequest

  constructor(config: HttpRequestOptions) {
    super()

    this._url = config.url
    this._method = config.method || 'GET'
    this._query = config.query || ''
    this._body = config.body
    this._timeout = config.timeout || REQUEST_TIMEOUT
    this._headers = config.headers || {}
    this._responseType = ''
    this._onBeforeHandlers = []
    this._onAfterHandlers = []
  }

  get xhr(): XMLHttpRequest {
    return this._xhr || new XMLHttpRequest()
  }

  setMethod(method: string): this {
    this._method = method

    return this
  }

  setUrl(url: string): this {
    this._url = url

    return this
  }

  setQueryParams(params: string | {}): this {
    this._query = params

    return this
  }

  setRequestHeader(field: string, value: string): this {
    this._headers[field] = value

    return this
  }

  setJsonBody(body: any): this {
    this._body = JSON.stringify(body)

    return this
  }

  setBody(body: any): this {
    this._body = body

    return this
  }

  setTimeout(timeout: number): this {
    this._timeout = timeout

    return this
  }

  setResponseType(type: ResponseType): this {
    this._responseType = type

    return this
  }

  // setToken(token: string): this {
  //   this.setRequestHeader('Authorization', `Bearer ${token}`)
  //
  //   return this
  // }

  get(): this {
    this.setMethod('GET')

    return this
  }

  post(body: any): this {
    this.setMethod('POST')
    this.setBody(body)

    return this
  }

  postJson(body: {}): this {
    this.setMethod('POST')
    this.setJsonBody(body)

    return this
  }

  put(body: any): this {
    this.setMethod('PUT')
    this.setBody(body)

    return this
  }

  putJson(body: {}): this {
    this.setMethod('PUT')
    this.setJsonBody(body)

    return this
  }

  patch(body: any): this {
    this.setMethod('PATCH')
    this.setBody(body)

    return this
  }

  patchJson(body: {}): this {
    this.setMethod('PATCH')
    this.setJsonBody(body)

    return this
  }

  delete(): this {
    this.setMethod('DELETE')

    return this
  }

  upload(body: FormData): this {
    this.setMethod('POST')
    this.setBody(body)
    this.setRequestHeader('Content-Type', 'multipart/form-data')

    return this
  }

  use(onRequest?: onRequestHandler, onResponse?: onResponseHandler): this {
    if (onRequest) {
      this.useBefore(onRequest)
    }

    if (onResponse) {
      this.useAfter(onResponse)
    }

    return this
  }

  useBefore(onRequest: onRequestHandler): this {
    this._onBeforeHandlers.push(onRequest)

    return this
  }

  useAfter(onResponse: onResponseHandler): this {
    this._onAfterHandlers.push(onResponse)

    return this
  }

  execute(): Promise<any> {
    let promise = new Promise((resolve: (xhr: XMLHttpRequest) => void) => {
      const xhr: XMLHttpRequest = (this._xhr = new XMLHttpRequest())

      xhr.timeout = this._timeout
      if (xhr.upload) {
        xhr.upload.onprogress = (event) => {
          this.emit('onProgress', event)
        }
      }

      this._onBeforeHandlers.forEach((handler: onRequestHandler) =>
        handler(this)
      )

      if (this._responseType) {
        xhr.responseType = this._responseType
      }

      this.emit('onRequest', this)

      let url = this._url
      if (this._query) {
        // todo проверять урл на наличие в нем queryString
        if (typeof this._query === 'string') {
          url += `?${this._query}`
        } else if (typeof this._query === 'object') {
          const query = Object.keys(this._query).map(
            (param) =>
              `${encodeURIComponent(param)}=${encodeURIComponent(
                this._query[param]
              )}`
          )
          url += `?${query.join('&')}`
        }
      }
      xhr.open(this._method, url, true)

      Object.keys(this._headers).forEach((prop) =>
        xhr.setRequestHeader(prop, this._headers[prop])
      )

      xhr.onreadystatechange = () => {
        if (xhr.readyState === XHR_READY_STATE_SUCCESS) {
          resolve(xhr)
        }
      }

      xhr.send(this._body)
    }).then(this._processResponse)

    this._onAfterHandlers.forEach((handler: onResponseHandler) => {
      promise = handler(promise, this)
    })

    return promise
      .then((result) => {
        this.emit('onResponseSuccess', result, this)

        return result
      })
      .catch((error) => {
        this.emit('onResponseError', error, this)

        return Promise.reject(error)
      })
  }

  private _processResponse = (xhr: XMLHttpRequest): Promise<any> => {
    const contentType = xhr.getResponseHeader('Content-Type') || ''
    const isJson =
      xhr.responseType === 'json' || contentType === 'application/json'

    try {
      // todo remove костыль
      const response = isJson
        ? JSON.parse(xhr.responseText || '{}')
        : xhr.response

      if (xhr.status !== STATUS_OK) {
        if (isJson) {
          return Promise.reject(response)
        }

        return Promise.reject(
          new ErrorResponse(xhr.status, response || xhr.statusText)
        )
      } else {
        return Promise.resolve(response)
      }
    } catch (error) {
      return Promise.reject(error)
    }
  }
}
